import { t, useLanguage } from "@/lib/i18n";
import { spouseIds } from "@/lib/spouses";
import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { lineageOptions } from "@/lib/lineageOptions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MemberDetailDrawer from "./MemberDetailDrawer";
import { toast } from "sonner";
import { displayMemberName } from "@/lib/memberName";

export default function AddRelativeDialog({ person, relation, persons, onClose, onCreate, onSaved }) {
  useLanguage();
  const [query, setQuery] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [saving, setSaving] = useState(false);
  const eligible = lineageOptions(persons, person, person.id);
  const parentSlot = person.gender === "Female" ? "mother_id" : "father_id";
  const search = query.trim().toLowerCase();
  const candidates = !search ? [] : (relation === "father" ? eligible.fathers : relation === "mother" ? eligible.mothers : relation === "spouse" ? eligible.spouses : eligible.children)
    .filter((p) => relation !== "spouse" || !spouseIds(person).includes(p.id))
    .filter((p) => relation !== "child" || (!(person.children_ids || []).includes(p.id) && !p[parentSlot]))
    .filter((p) => [p.full_name, p.caste, p.gotra, p.birth_place, p.birth_date, p.email, p.phone_number]
      .some((value) => value?.toLowerCase().includes(search)));
  const byId = Object.fromEntries(persons.map((p) => [p.id, p]));
  const confirm = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/persons/${person.id}/relatives`, { relation, member_id: candidate.id });
      toast.success(data.status === "Pending" ? t("Relationship request submitted for admin approval. Track it in My Requests.") : t("Relationship saved successfully."));
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(t(formatApiErrorDetail(error.response?.data?.detail)));
    } finally {
      setSaving(false);
    }
  };
  return <>
    <Dialog open={!candidate} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>{t("Add {relation} for {name}", { relation: t(relation), name: displayMemberName(person) })}</DialogTitle>
          <DialogDescription>{t("Select an existing member to review their details, or create a new member.")}</DialogDescription>
        </DialogHeader>
        <Button onClick={() => onCreate(person, relation)} data-testid="relative-create-new">{t("Create New Member")}</Button>
        <Input aria-label={t("Search existing members")} placeholder={t("Search by name, caste, gotra, birth date, place, email or phone")} value={query} onChange={(e) => setQuery(e.target.value)} />
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {candidates.map((member) => <button key={member.id} type="button" onClick={() => setCandidate(member)}
            className="w-full text-left rounded-lg border p-3 hover:bg-amber-50 focus-visible:outline-amber-600" data-testid={`relative-candidate-${member.id}`}>
            <span className="block font-medium">{displayMemberName(member)}</span>
            <span className="block text-sm text-stone-600">{[member.caste, member.gotra, t(member.gender), member.birth_date, member.birth_place].filter(Boolean).join(" · ")}</span>
            <span className="block text-xs text-stone-500">{[
              byId[member.father_id] && t("Father: {0}", { 0: displayMemberName(byId[member.father_id]) }),
              byId[member.mother_id] && t("Mother: {0}", { 0: displayMemberName(byId[member.mother_id]) }),
            ].filter(Boolean).join(" · ")}</span>
          </button>)}
          {!candidates.length && <p className="text-sm text-stone-500">{search ? t("No eligible members found. Try another search or create a new member.") : t("Type in the search box to find an existing member.")}</p>}
        </div>
      </DialogContent>
    </Dialog>
    {candidate && <MemberDetailDrawer person={candidate} persons={persons} previewOnly
      onClose={() => !saving && setCandidate(null)}
      selectionActions={<div className="space-y-2 mb-4">
        <p className="text-sm">{t("Add {candidate} as {relation} of {name}?", { candidate: displayMemberName(candidate), relation: t(relation), name: displayMemberName(person) })}</p>
        <div className="flex gap-2">
          <Button disabled={saving} onClick={confirm} data-testid="relative-confirm">{saving ? t("Adding…") : t("Add as {0}", { 0: t(relation) })}</Button>
          <Button variant="outline" disabled={saving} onClick={() => setCandidate(null)}>{t("Back to results")}</Button>
        </div>
      </div>} />}
  </>;
}
